import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  LiksDocumentPreviewModal,
  type LiksDocumentPreviewItem,
} from '../../../components/liks/LiksDocumentPreviewModal/LiksDocumentPreviewModal';
import { clampIndex } from '../../../components/liks/LiksDocumentPreviewModal/documentPreviewKind';
import { isForbiddenOrNotFoundApiError } from '../../../query/apiErrorMessage';
import { useDocumentPreviewPrefetch } from '../hooks/useDocumentPreviewPrefetch';
import { useKnowledgeDocumentDownloadQuery } from '../queries/useKnowledgeDocumentDownloadQuery';
import { useKnowledgeDocumentQuery } from '../queries/useKnowledgeDocumentQuery';

export type { LiksDocumentPreviewItem };

export type KnowledgeDocumentPreviewModalProps = {
  isOpen: boolean;
  knowledgeBaseId: string;
  documentId: string;
  title: string;
  items?: LiksDocumentPreviewItem[];
  initialIndex?: number;
  closeLabel: string;
  loadingLabel: string;
  errorLabel: string;
  inaccessibleLabel?: string;
  unavailableLabel: string;
  notReadyLabel?: string;
  onClose: () => void;
};

export function KnowledgeDocumentPreviewModal({
  isOpen,
  knowledgeBaseId,
  documentId,
  title,
  items,
  initialIndex = 0,
  closeLabel,
  loadingLabel,
  errorLabel,
  inaccessibleLabel,
  unavailableLabel,
  notReadyLabel,
  onClose,
}: KnowledgeDocumentPreviewModalProps) {
  const queryClient = useQueryClient();

  const resolvedItems = useMemo((): LiksDocumentPreviewItem[] => {
    if (items != null && items.length >= 1) {
      return items;
    }
    return [{ id: documentId, title, isReadyForPreview: true }];
  }, [items, documentId, title]);

  const itemsIdentity = resolvedItems.map((item) => item.id).join('|');
  const navigationResetKey = `${isOpen}:${itemsIdentity}:${initialIndex}`;
  const [activeIndex, setActiveIndex] = useState(() =>
    clampIndex(initialIndex, resolvedItems.length),
  );
  const [navigationKey, setNavigationKey] = useState(navigationResetKey);
  if (navigationKey !== navigationResetKey) {
    setNavigationKey(navigationResetKey);
    if (isOpen) {
      setActiveIndex(clampIndex(initialIndex, resolvedItems.length));
    }
  }

  const activeItem = resolvedItems[activeIndex] ?? resolvedItems[0];
  const activeDocumentId = activeItem.id;
  const activeTitle = activeItem.title;
  const isActiveReady = activeItem.isReadyForPreview;
  const hasMultipleItems = resolvedItems.length > 1;

  useDocumentPreviewPrefetch({
    isOpen,
    hasMultipleItems,
    activeIndex,
    knowledgeBaseId,
    resolvedItems,
    queryClient,
  });

  const documentQuery = useKnowledgeDocumentQuery(
    knowledgeBaseId,
    activeDocumentId,
    { enabled: isOpen && isActiveReady },
  );
  const downloadQuery = useKnowledgeDocumentDownloadQuery(
    knowledgeBaseId,
    activeDocumentId,
    { enabled: isOpen && isActiveReady },
  );

  const blob = downloadQuery.data ?? null;
  const fileName =
    documentQuery.data?.fileName?.trim() ||
    documentQuery.data?.displayName?.trim() ||
    activeTitle;
  const mimeType = documentQuery.data?.mimeType ?? blob?.type ?? null;

  const displayTitle = useMemo(() => {
    const metaFileName = documentQuery.data?.fileName?.trim();
    const displayName = documentQuery.data?.displayName?.trim();
    return displayName || metaFileName || activeTitle;
  }, [
    activeTitle,
    documentQuery.data?.displayName,
    documentQuery.data?.fileName,
  ]);

  const isLoading = downloadQuery.isLoading || downloadQuery.isFetching;
  const isError = downloadQuery.isError;
  const isInaccessible =
    inaccessibleLabel != null &&
    (isForbiddenOrNotFoundApiError(downloadQuery.error) ||
      isForbiddenOrNotFoundApiError(documentQuery.error));

  return (
    <LiksDocumentPreviewModal
      isOpen={isOpen}
      items={resolvedItems}
      activeIndex={activeIndex}
      onActiveIndexChange={setActiveIndex}
      displayTitle={displayTitle}
      blob={blob}
      mimeType={mimeType}
      fileName={fileName}
      isLoading={isLoading}
      isError={isError}
      isInaccessible={isInaccessible}
      isDownloadSuccess={downloadQuery.isSuccess}
      closeLabel={closeLabel}
      loadingLabel={loadingLabel}
      errorLabel={errorLabel}
      inaccessibleLabel={inaccessibleLabel}
      unavailableLabel={unavailableLabel}
      notReadyLabel={notReadyLabel}
      onClose={onClose}
    />
  );
}
